/**
 * Colourings of the tiling that respect its symmetry ("perfect colourings").
 *
 * A colouring of the copies of the fundamental domain is *perfect* when every symmetry of the
 * pattern permutes the colours. Such colourings correspond to homomorphisms ρ from the symmetry
 * group onto a permutation group of the colours: the copy h·W gets colour ρ(h)(0).
 *
 *  - Reflection parity: ρ(g) = (number of reflections in g) mod 2 — two colours.
 *  - Rotation classes: a homomorphism of the rotation group ⟨x, y | x^p = y^q = (xy)^r = 1⟩
 *    into S_k, found here by a small exhaustive search. We ask that ρ(x) contain a p-cycle
 *    through the base colour 0, so the p copies around every tile centre get p different
 *    colours ("the colours rotate with the motif").
 *
 * For {7,3} the smallest such colouring has k = 7: the rotation group (2,3,7) is perfect (no
 * abelian quotients), and its smallest non-trivial quotient is PSL(2,7) of order 168, which
 * acts on 7 points (Klein's quartic, 1879).
 */

export type Perm = readonly number[];

export const identityPerm = (k: number): number[] => Array.from({ length: k }, (_, i) => i);

/** (σ∘τ)(i) = σ(τ(i)). */
export function composePerm(s: Perm, t: Perm): number[] {
  return t.map((x) => s[x]);
}

export function invertPerm(s: Perm): number[] {
  const inv = new Array<number>(s.length);
  s.forEach((x, i) => (inv[x] = i));
  return inv;
}

export function cycleLengths(s: Perm): number[] {
  const seen = new Array<boolean>(s.length).fill(false);
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    if (seen[i]) continue;
    let len = 0;
    for (let j = i; !seen[j]; j = s[j]) {
      seen[j] = true;
      len++;
    }
    out.push(len);
  }
  return out;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
const lcm = (a: number, b: number): number => (a / gcd(a, b)) * b;

export const permOrder = (s: Perm): number => cycleLengths(s).reduce(lcm, 1);

export function permPower(s: Perm, n: number): number[] {
  let out = identityPerm(s.length);
  for (let i = 0; i < n; i++) out = composePerm(s, out);
  return out;
}

/** Are the generators' orbits a single orbit (is the action transitive)? */
export function isTransitive(gens: readonly Perm[], k: number): boolean {
  const parent = identityPerm(k);
  const find = (i: number): number => {
    while (parent[i] !== i) i = parent[i] = parent[parent[i]];
    return i;
  };
  for (const g of gens) for (let i = 0; i < k; i++) parent[find(i)] = find(g[i]);
  const root = find(0);
  for (let i = 1; i < k; i++) if (find(i) !== root) return false;
  return true;
}

/** Order of the permutation group generated by gens (breadth-first closure, capped). */
export function groupOrder(gens: readonly Perm[], cap = 100000): number {
  const k = gens[0]?.length ?? 0;
  const key = (s: Perm) => s.join(',');
  const start = identityPerm(k);
  const seen = new Set<string>([key(start)]);
  const queue: Perm[] = [start];
  while (queue.length > 0) {
    const s = queue.shift()!;
    for (const g of gens) {
      const t = composePerm(g, s);
      const kt = key(t);
      if (!seen.has(kt)) {
        seen.add(kt);
        if (seen.size > cap) return Infinity;
        queue.push(t);
      }
    }
  }
  return seen.size;
}

/** Partitions of n into parts from `allowed` (non-increasing), each listed once. */
function partitions(n: number, allowed: number[], maxPart = n): number[][] {
  if (n === 0) return [[]];
  const out: number[][] = [];
  for (const a of allowed) {
    if (a > maxPart || a > n) continue;
    for (const rest of partitions(n - a, allowed, a)) out.push([a, ...rest]);
  }
  return out;
}

const divisors = (n: number): number[] =>
  Array.from({ length: n }, (_, i) => i + 1).filter((d) => n % d === 0);

/** The permutation with consecutive cycles of the given lengths: (0 1 … a−1)(a …)… */
function permFromCycleType(lengths: number[]): number[] {
  const k = lengths.reduce((s, x) => s + x, 0);
  const s = identityPerm(k);
  let at = 0;
  for (const L of lengths) {
    for (let j = 0; j < L; j++) s[at + j] = at + ((j + 1) % L);
    at += L;
  }
  return s;
}

/** Calls visit(σ) for every σ ∈ S_k all of whose cycle lengths divide m. Stops when visit returns true. */
function forEachPermOfOrderDividing(
  k: number,
  m: number,
  visit: (s: number[]) => boolean,
): boolean {
  const allowed = divisors(m);
  const s = new Array<number>(k).fill(-1);
  const used = new Array<boolean>(k).fill(false);
  const rec = (): boolean => {
    let first = -1;
    for (let i = 0; i < k; i++)
      if (!used[i]) {
        first = i;
        break;
      }
    if (first < 0) return visit(s.slice());
    const free: number[] = [];
    for (let i = first + 1; i < k; i++) if (!used[i]) free.push(i);
    for (const L of allowed) {
      if (L - 1 > free.length) continue;
      // choose an ordered sequence of L−1 distinct elements from `free` to follow `first`
      const seq: number[] = [];
      const pick = (): boolean => {
        if (seq.length === L - 1) {
          const cyc = [first, ...seq];
          for (let j = 0; j < L; j++) {
            s[cyc[j]] = cyc[(j + 1) % L];
            used[cyc[j]] = true;
          }
          const stop = rec();
          for (const c of cyc) {
            used[c] = false;
            s[c] = -1;
          }
          return stop;
        }
        for (const f of free) {
          if (used[f] || seq.includes(f)) continue;
          seq.push(f);
          const stop = pick();
          seq.pop();
          if (stop) return true;
        }
        return false;
      };
      if (pick()) return true;
    }
    return false;
  };
  return rec();
}

export interface RotationColouring {
  k: number;
  /** Images of the rotations x (about P), y (about Q), z (about R); x·y·z = 1. */
  X: number[];
  Y: number[];
  Z: number[];
  /** Order of the colour-permutation group ⟨X, Y⟩. */
  groupOrder: number;
}

export interface ColouringSearchOptions {
  maxK?: number;
  /** Budget on candidate checks, to bound the work for unusual custom groups. */
  maxChecks?: number;
}

/**
 * Smallest k ≤ maxK admitting a transitive homomorphism ρ: ⟨x, y⟩ → S_k with ρ(x)^p = ρ(y)^q =
 * (ρ(x)ρ(y))^r = 1, where ρ(x) has a p-cycle through 0. Deterministic. Returns null if none is
 * found within the limits.
 */
export function findRotationColouring(
  p: number,
  q: number,
  r: number,
  opts: ColouringSearchOptions = {},
): RotationColouring | null {
  const maxK = opts.maxK ?? 8;
  let checks = 0;
  const maxChecks = opts.maxChecks ?? 2_000_000;
  const pDivs = divisors(p);
  for (let k = Math.max(2, p); k <= maxK; k++) {
    // X: cycle type with one p-cycle (through 0) followed by cycles whose lengths divide p.
    const types = partitions(k - p, pDivs).map((rest) => [p, ...rest]);
    for (const type of types) {
      const X = permFromCycleType(type);
      let found: RotationColouring | null = null;
      forEachPermOfOrderDividing(k, q, (Y) => {
        if (++checks > maxChecks) return true;
        const XY = composePerm(X, Y);
        if (r % permOrder(XY) !== 0) return false;
        if (!isTransitive([X, Y], k)) return false;
        const Z = invertPerm(XY);
        found = { k, X, Y, Z, groupOrder: groupOrder([X, Y]) };
        return true;
      });
      if (found) return found;
      if (checks > maxChecks) return null;
    }
  }
  return null;
}

/**
 * ρ of each ordered pair of reflections m_a·m_b, indexed [3a + b]. These are the images of the
 * even words the fold produces two letters at a time: m0m1 = x, m1m2 = y, m2m0 = z and their
 * inverses; m_a·m_a = 1.
 */
export function pairPermutations(c: RotationColouring): number[][] {
  const id = identityPerm(c.k);
  const xi = invertPerm(c.X);
  const yi = invertPerm(c.Y);
  const zi = invertPerm(c.Z);
  return [
    id, // m0 m0
    c.X, // m0 m1 = x
    zi, // m0 m2 = z⁻¹
    xi, // m1 m0 = x⁻¹
    id, // m1 m1
    c.Y, // m1 m2 = y
    c.Z, // m2 m0 = z
    yi, // m2 m1 = y⁻¹
    id, // m2 m2
  ];
}

/**
 * ρ(g) for the rotation g given by a word of mirror indices (as produced by the fold, applied
 * left to right, of even length): g = m_{w_n}···m_{w_1}.
 */
export function wordPermutation(word: readonly number[], pairs: number[][]): number[] {
  if (word.length % 2 !== 0) throw new Error('wordPermutation needs an even word');
  let P = identityPerm(pairs[0].length);
  for (let i = 0; i < word.length; i += 2) {
    const first = word[i];
    const second = word[i + 1];
    P = composePerm(pairs[3 * second + first], P);
  }
  return P;
}

/** Packs a permutation of ≤ 8 points into a 32-bit integer, 4 bits per image. */
export function packPerm(s: Perm): number {
  if (s.length > 8) throw new RangeError('packPerm supports at most 8 points');
  let v = 0;
  for (let i = 0; i < 8; i++) v |= (i < s.length ? s[i] : i) << (4 * i);
  return v >>> 0;
}

export function unpackPerm(v: number, k: number): number[] {
  return Array.from({ length: k }, (_, i) => (v >>> (4 * i)) & 15);
}

/** All elements of the permutation group generated by gens (breadth-first, capped). */
export function groupElements(gens: readonly Perm[], cap = 5000): number[][] | null {
  const k = gens[0]?.length ?? 0;
  const start = identityPerm(k);
  const seen = new Map<string, number[]>([[start.join(','), start]]);
  const queue: number[][] = [start];
  while (queue.length > 0) {
    const s = queue.shift()!;
    for (const g of gens) {
      const t = composePerm(g, s);
      const key = t.join(',');
      if (!seen.has(key)) {
        if (seen.size >= cap) return null;
        seen.set(key, t);
        queue.push(t);
      }
    }
  }
  return [...seen.values()];
}

/** Is ⟨X, Y⟩ the dihedral group of order 2k with X a k-cycle? */
export function isDihedral(c: { k: number; X: Perm; Y: Perm; groupOrder: number }): boolean {
  if (c.groupOrder !== 2 * c.k || cycleLengths(c.X).length !== 1) return false;
  const els = groupElements([c.X, c.Y], 4 * c.k);
  if (!els) return false;
  const Xi = invertPerm(c.X);
  const id = identityPerm(c.k).join(',');
  return els.some((t) => {
    if (composePerm(t, t).join(',') !== id || t.join(',') === id) return false;
    return composePerm(t, composePerm(c.X, t)).join(',') === Xi.join(',');
  });
}
